import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DollarSign, Wallet, TrendingUp, Calendar, ChevronDown, ChevronUp } from "lucide-react";

const fmtUsd = (c) => `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;

/**
 * Coach Earnings + Incoming Bookings card.
 *
 * Drops onto the CoachLessonOfferings page so coaches see their pending payouts
 * and the schedule of confirmed sessions in one place.
 */
export default function CoachEarningsCard() {
  const [earnings, setEarnings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showBookings, setShowBookings] = useState(true);

  const load = async () => {
    const [{ data: e }, { data: b }] = await Promise.all([
      api.get("/lessons/earnings/me"),
      api.get("/lessons/bookings/coach/me"),
    ]);
    setEarnings(e);
    setBookings(b.bookings || []);
  };
  useEffect(() => { load(); }, []);

  if (!earnings) return null;

  return (
    <div className="space-y-4" data-testid="coach-earnings-card">
      <div className="grid sm:grid-cols-3 gap-3">
        <KPI icon={Wallet} label="Pending payout" value={fmtUsd(earnings.pending_payout.amount_cents)} sub={`${earnings.pending_payout.booking_count} booking${earnings.pending_payout.booking_count === 1 ? "" : "s"}`} accent="#22D3EE" testid="kpi-pending" />
        <KPI icon={DollarSign} label="Paid out" value={fmtUsd(earnings.paid_out.amount_cents)} sub={`${earnings.paid_out.booking_count} sessions`} accent="#34D399" testid="kpi-paid" />
        <KPI icon={TrendingUp} label="Lifetime gross" value={fmtUsd(earnings.lifetime_gross_cents)} sub={`${earnings.platform_fee_pct}% platform fee · ${100 - earnings.platform_fee_pct}% your cut`} accent="#FBBF24" testid="kpi-gross" />
      </div>

      <div className="rounded-xl ink-card p-5">
        <button onClick={() => setShowBookings((v) => !v)} className="w-full flex items-center justify-between" data-testid="incoming-toggle">
          <div className="text-left">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Incoming sessions</div>
            <h3 className="font-display font-bold text-lg text-white mt-1">{bookings.length} booking{bookings.length === 1 ? "" : "s"}</h3>
          </div>
          {showBookings ? <ChevronUp className="h-4 w-4 text-white/40" /> : <ChevronDown className="h-4 w-4 text-white/40" />}
        </button>

        {showBookings && (
          <div className="mt-3 space-y-2">
            {bookings.length === 0 && (
              <div className="text-xs text-white/40 italic py-4 text-center">No bookings yet — share your marketplace link to start earning.</div>
            )}
            {bookings.map((b) => {
              const isPaid = b.payout_status === "paid";
              const isConfirmed = b.status === "confirmed";
              return (
                <div key={b.booking_id} className="rounded-lg bg-white/[0.03] border border-white/10 p-3 grid grid-cols-[1fr_auto] gap-3 items-center" data-testid={`incoming-${b.booking_id}`}>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-white text-sm truncate">{b.student_name} · {b.lesson_label}</div>
                    <div className="text-xs text-white/55 flex items-center gap-2 mt-0.5">
                      <Calendar className="h-3 w-3" />
                      <span className="font-mono-num">{new Date(b.slot_iso).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-base text-white">{fmtUsd(b.coach_payout_cents)}</div>
                    <div className="text-[10px] uppercase tracking-wide font-bold mt-0.5"
                      style={{ color: !isConfirmed ? "#FCD34D" : isPaid ? "#6EE7B7" : "#22D3EE" }}>
                      {!isConfirmed ? "Awaiting pay" : isPaid ? "Paid out" : "Pending payout"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-200/90">
        <b>Payouts:</b> ArmIQ collects 100% of student payments via Stripe and disburses your {100 - earnings.platform_fee_pct}% share monthly via ACH.
        Connect-based instant payouts ship in v3.10 (requires your own Stripe account).
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-xl ink-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] font-bold uppercase" style={{ color: accent }}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="font-display font-black text-3xl text-white tracking-tight mt-1">{value}</div>
      <div className="text-xs text-white/50 mt-1">{sub}</div>
    </div>
  );
}
